import React, { useState } from "react";
import { updateProfile } from "@/services/auth.service";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { User as UserIcon } from "lucide-react";
import { Section, Hint } from "./SettingsShared";

const ProfileTab = ({ user, refreshUser }) => {
  const [form, setForm] = useState({
    name: user.name || "",
    display_name: user.display_name || "",
    phone: user.phone || "",
    location_city: user.location_city || "",
    location_state: user.location_state || "",
    location_country: user.location_country || "",
    timezone: user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    business_name: user.business_name || "",
    role_preference: user.role_preference || "Both",
    picture: user.picture || "",
    default_currency: user.default_currency || "USD",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Full name required"); return; }
    setSaving(true);
    try {
      await updateProfile(form);
      await refreshUser();
      toast.success("Profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Section title="Profile" description="Your identity on PAYD. One account handles paying and getting paid." data-testid="profile-section">
      <div className="flex items-start gap-5 flex-wrap">
        <div className="shrink-0">
          <div className="w-20 h-20 rounded-full bg-secondary border border-border overflow-hidden flex items-center justify-center">
            {form.picture ? <img src={form.picture} alt="" className="w-full h-full object-cover" /> : <UserIcon className="w-8 h-8 text-muted-foreground" />}
          </div>
        </div>
        <div className="flex-1 min-w-0 w-full">
          <Label>Profile photo URL</Label>
          <Input value={form.picture} onChange={(e) => setForm({ ...form, picture: e.target.value })} placeholder="https://…" className="mt-1.5 h-11" data-testid="profile-picture-input" />
          <Hint>Paste a public image URL. Object-storage upload coming soon.</Hint>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Full name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" data-testid="profile-name-input" />
        </div>
        <div>
          <Label>Display name</Label>
          <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Shown to counterparties" className="mt-1.5 h-11" data-testid="profile-display-input" />
        </div>
        <div>
          <Label>Email</Label>
          <Input value={user.email} disabled className="mt-1.5 h-11" />
          <Hint>Email is controlled by your auth provider ({user.auth_provider}).</Hint>
        </div>
        <div>
          <Label>Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 555 000 0000" className="mt-1.5 h-11" data-testid="profile-phone-input" />
        </div>
        <div>
          <Label>City</Label>
          <Input value={form.location_city} onChange={(e) => setForm({ ...form, location_city: e.target.value })} className="mt-1.5 h-11" data-testid="profile-city-input" />
        </div>
        <div>
          <Label>State / Province</Label>
          <Input value={form.location_state} onChange={(e) => setForm({ ...form, location_state: e.target.value })} className="mt-1.5 h-11" data-testid="profile-state-input" />
        </div>
        <div>
          <Label>Country</Label>
          <Input value={form.location_country} onChange={(e) => setForm({ ...form, location_country: e.target.value })} className="mt-1.5 h-11" data-testid="profile-country-input" />
        </div>
        <div>
          <Label>Timezone</Label>
          <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="America/Los_Angeles" className="mt-1.5 h-11" data-testid="profile-timezone-input" />
        </div>
        <div>
          <Label>Business name (optional)</Label>
          <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1.5 h-11" data-testid="profile-business-input" />
        </div>
        <div>
          <Label>Role preference</Label>
          <select value={form.role_preference} onChange={(e) => setForm({ ...form, role_preference: e.target.value })} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="profile-role-preference-select">
            <option value="Freelancer">Freelancer</option>
            <option value="Business">Business</option>
            <option value="Both">Both</option>
          </select>
        </div>
        <div>
          <Label>Default currency</Label>
          <select value={form.default_currency} onChange={(e) => setForm({ ...form, default_currency: e.target.value })} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="profile-default-currency-select">
            {["USD", "EUR", "GBP", "CAD", "AUD", "INR", "JPY", "SGD"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="profile-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </Section>
  );
};

export default ProfileTab;
